<template>
  <div class="json-viewer">
    <div class="viewer-header">
      <!-- <h3>Process Log Data</h3> -->
      <a-typography-title :level="5" class="h5-title">
        Process Log Data
      </a-typography-title>
      <div class="controls">
        <!-- <a-button @click="toggleView" class="btn-toggle"> -->
        <a-button
          type="primary"
          size="small"
          shape="round"
          class="btn-toggle"
          @click="toggleView"
        >
          {{ viewMode === 'formatted' ? 'Show Raw' : 'Show Formatted' }}
        </a-button>
        <a-button
          type="primary"
          size="small"
          shape="round"
          class="btn-copy"
          @click="copyToClipboard"
        >
          <!-- <a-button @click="copyToClipboard" class="btn-copy"> -->
          {{ copied ? 'Copied!' : 'Copy JSON' }}
        </a-button>
      </div>
    </div>

    <div class="content-area">
      <!-- Formatted View -->
      <div v-if="viewMode === 'formatted'" class="formatted-view">
        <!-- Process Events Section -->
        <div
          v-if="hasNumericEntries"
          class="section"
          :class="sectionClasses.numeric"
        >
          <a-typography-text strong class="mb-2">
            Process Events
          </a-typography-text>
          <div class="log-entries">
            <div
              v-for="(value, key) in numericEntries"
              :key="key"
              class="log-entry"
            >
              <span class="entry-key">{{ key }}</span>
              <span class="entry-value">{{ value }}</span>
            </div>
          </div>
        </div>

        <!-- Completion Status Section -->
        <div
          v-if="hasBooleanEntries"
          class="section"
          :class="sectionClasses.boolean"
        >
          <a-typography-text strong class="mb-2">
            Completion Status
          </a-typography-text>
          <div class="status-entries">
            <div
              v-for="entry in booleanEntries"
              :key="entry.key"
              class="status-entry"
            >
              <span class="status-key">{{ entry.key }}</span>
              <span
                :class="[
                  'status-badge',
                  entry.value ? 'complete' : 'incomplete',
                ]"
              >
                {{ entry.value ? '✓ Complete' : '✗ Incomplete' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="!hasNumericEntries && !hasBooleanEntries"
          class="empty-state"
        >
          <a-empty description="No process data available" />
        </div>
      </div>

      <!-- Raw JSON View -->
      <div v-else class="raw-view-container">
        <pre class="raw-view"><code>{{ formattedJson }}</code></pre>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JsonViewer',
  props: {
    jsonData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      viewMode: 'formatted', // 'formatted' or 'raw'
      copied: false,
    };
  },
  computed: {
    numericEntries() {
      const entries = {};
      for (const [key, value] of Object.entries(this.jsonData)) {
        // Include all entries except boolean values
        if (typeof value !== 'boolean') {
          entries[key] = value;
        }
      }
      return entries;
    },
    booleanEntries() {
      return Object.entries(this.jsonData)
        .filter(([key, value]) => typeof value === 'boolean')
        .map(([key, value]) => ({ key, value }));
    },
    formattedJson() {
      return JSON.stringify(this.jsonData, null, 2);
    },
    hasNumericEntries() {
      return Object.keys(this.numericEntries).length > 0;
    },
    hasBooleanEntries() {
      return this.booleanEntries.length > 0;
    },
    sectionClasses() {
      // Dynamic height allocation based on content availability
      if (this.hasNumericEntries && this.hasBooleanEntries) {
        // Both sections present - equal height
        return {
          numeric: 'section-half',
          boolean: 'section-half',
        };
      } else if (this.hasNumericEntries && !this.hasBooleanEntries) {
        // Only numeric entries - full height
        return {
          numeric: 'section-full',
          boolean: '',
        };
      } else if (!this.hasNumericEntries && this.hasBooleanEntries) {
        // Only boolean entries - full height
        return {
          numeric: '',
          boolean: 'section-full',
        };
      }
      return {
        numeric: '',
        boolean: '',
      };
    },
  },
  methods: {
    toggleView() {
      this.viewMode = this.viewMode === 'formatted' ? 'raw' : 'formatted';
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.formattedJson);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
  },
};
</script>

<style scoped>
.json-viewer {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  /* background: #f8f9fa; */
  border-radius: 8px;
  max-width: 100%;
  /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); */
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent outer div from scrolling */
}

.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 10px;
  flex-shrink: 0; /* Prevent header from shrinking */
}

.h5-title {
  margin: 0;
  color: #2c3e50;
}

.controls {
  display: flex;
  gap: 10px;
}

.btn-copy {
  background: #2ecc71;
  border: 1px solid #2ecc71;
  color: white;
}

.btn-copy:hover {
  background: #27ae60;
}

/* Content Area - Flexible and scrollable */
.content-area {
  flex: 1;
  min-height: 0; /* Important for flexbox scrolling */
  overflow: hidden;
}

/* Formatted View */
.formatted-view {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
}

.section {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Dynamic section height classes */
.section-full {
  flex: 1; /* Take full available height */
}

.section-half {
  flex: 1; /* Equal height when both sections present */
}

/* Empty state */
.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.section h4 {
  margin: 0 0 12px 0;
  color: #34495e;
  font-size: 1.2em;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  flex-shrink: 0;
}

.log-entries,
.status-entries {
  flex: 1;
  overflow-y: auto; /* Only vertical scrolling for formatted sections */
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px; /* Space for scrollbar */
}

.log-entry {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #3498db;
  transition: transform 0.2s;
  flex-shrink: 0;
  /* Removed min-width: max-content to allow wrapping */
}

.log-entry:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.entry-key {
  font-weight: 600;
  color: #7f8c8d;
  min-width: 40px;
  font-family: 'Courier New', monospace;
  /* Removed white-space: nowrap to allow wrapping if needed */
}

.entry-value {
  color: #2c3e50;
  flex: 1;
  word-break: break-word;
  /* Keep pre-wrap for proper text wrapping */
  white-space: pre-wrap;
}

.status-entry {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #2ecc71;
  flex-shrink: 0;
  /* Removed min-width: max-content to allow wrapping */
}

.status-key {
  font-weight: 600;
  color: #2c3e50;
  /* Allow wrapping if key is too long */
  word-break: break-word;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap; /* Keep badge text from wrapping */
}

.status-badge.complete {
  background: #d4edda;
  color: #155724;
}

.status-badge.incomplete {
  background: #f8d7da;
  color: #721c24;
}

/* Raw View */
.raw-view-container {
  height: 100%;
  overflow: hidden;
}

.raw-view {
  background: #2c3e50;
  color: #ecf0f1;
  padding: 20px;
  border-radius: 6px;
  overflow: auto; /* Both horizontal and vertical scrolling for raw JSON */
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  height: 100%;
  box-sizing: border-box;
  white-space: pre; /* Preserve JSON formatting - enables horizontal scroll */
}

.raw-view code {
  color: #ecf0f1;
}

/* Scrollbar Styling - Only vertical for log/status entries */
.log-entries::-webkit-scrollbar,
.status-entries::-webkit-scrollbar {
  width: 8px;
  /* No height specified - only vertical scrollbar */
}

.log-entries::-webkit-scrollbar-track,
.status-entries::-webkit-scrollbar-track {
  background: #ecf0f1;
  border-radius: 4px;
}

.log-entries::-webkit-scrollbar-thumb,
.status-entries::-webkit-scrollbar-thumb {
  background: #95a5a6;
  border-radius: 4px;
}

.log-entries::-webkit-scrollbar-thumb:hover,
.status-entries::-webkit-scrollbar-thumb:hover {
  background: #7f8c8d;
}

/* Raw view scrollbars - Both horizontal and vertical */
.raw-view::-webkit-scrollbar {
  width: 8px;
  height: 8px; /* Both vertical and horizontal scrollbars */
}

.raw-view::-webkit-scrollbar-track {
  background: #34495e;
  border-radius: 4px;
}

.raw-view::-webkit-scrollbar-thumb {
  background: #95a5a6;
  border-radius: 4px;
}

.raw-view::-webkit-scrollbar-thumb:hover {
  background: #7f8c8d;
}

/* Corner where vertical and horizontal scrollbars meet - only for raw view */
.raw-view::-webkit-scrollbar-corner {
  background: #34495e;
}

/* Firefox scrollbar styling */
.log-entries,
.status-entries {
  scrollbar-width: thin;
  scrollbar-color: #95a5a6 #ecf0f1;
}

.raw-view {
  scrollbar-width: thin;
  scrollbar-color: #95a5a6 #34495e;
}
</style>
